/**
 * Sidebar(left) section of editor page
 */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { Tooltip, RadioGroup, Button } from 'wix-style-react'
import { Plus, ToggleOff } from 'wix-style-react/src/Icons'
import _ from 'lodash'

import { api } from '../../../api/integrationAPI'
import { limitCharacters } from 'utils/utils'
import { IconCircle, HELVETICA_ITALIC, GRAY, LIGHTEST_GRAY, BLUE, HELVETICA } from '../../styles'
import * as sidebarSceneType from '../../../constants/sidebarSceneType'
import { getTemplateInfo } from 'templates/templates'
import { addSlide, setNumberOfSlides } from 'controllers/logic'
import config from 'config'

const Container = styled.div`
  width: 250px; padding: 20px;
  border-right: 1px solid ${LIGHTEST_GRAY};
  box-shadow: 1px 0 5px 0 rgba(41, 85, 115, 0.21);
  position: relative;
  z-index: 10;

  background: white;
  overflow-y: scroll;

  ::-webkit-scrollbar {
      width: 8px;
  }
  ::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.1);
      border-radius: 10px;
      background-color: white;
  }
  ::-webkit-scrollbar-thumb {
      border-bottom-left-radius: 15px;
      border-bottom-right-radius: 15px;
      // background-color: rgba(131,203,202,0.2);
      // -webkit-box-shadow: inset 0 0 6px rgba(131,203,202,0.02);
      -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.15);
  }
`

const ScenesTitle = styled.div`
  ${HELVETICA_ITALIC}
  font-size: 18px;
  font-style: italic;
  text-align: center;
  padding: 5px 0;
  color: ${GRAY};
  margin-right: 8px;
`
const Scene = styled.div`
  position: relative;
  height: 240px;
  width: 135px;
  margin: 10px auto;
  cursor: pointer;
  border: 5px solid ${props => props.selected ? BLUE : '#fff'};
  border-radius: 2px;
  background-color: #333;
  background-image: url(${props => props.sceneUrl});
  background-size: ${props => props.isSmall ? '50px' : 'cover'};
  background-position: center;
  background-repeat: no-repeat;
  transition: all 0.2s ease-in-out;

  &:before {
    position: relative;
    font-weight: 300;
    font-size: 14px;
    color: ${GRAY};
    content: '${props => props.number}';
    left: -28px;
  }

  ${props => !props.selected
  ? `& > div {
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.15s ease-out, visibility 0s ease-out 0.15s;
    }
    &:hover > div {
      visibility: visible;
      opacity: 1;
      transition: opacity 0.2s ease-out;
    }`
  : ''}
`

const ScenesLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`
const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 10px 0px;
  cursor: pointer;
  padding-left: 3em;
`
const ProductLabel = styled.div`
  ${HELVETICA}
  font-size: 16px;
`
const ProductsContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;

  & > button {
    padding: 0 12px;
  }

  & > button > div {
    font-size: 13px !important;
  }

  ${props => !props.selected
  ? `& > button {
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.15s ease-out, visibility 0s ease-out 0.15s;
    }
    &:hover > button {
      visibility: visible;
      opacity: 1;
      transition: opacity 0.2s ease-out;
    }`
  : ''}
`
const DelSceneButtonContainer = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
`
const DelButton = styled.div`
  width: 5em;
  height: 5em;
  background-color: #FFF;
  border-radius: 50%;
  border-color: ${BLUE};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${BLUE};

  &:hover {
    background-color: #dbedfc;
  }
`

const defaultUrl = `https://firebasestorage.googleapis.com/v0/b/${config.storageBucket}/o/templates%2FoutSide%2Fbg-showcase-1.jpg?alt=media&token=6e5a3aef-d50a-4498-94cf-47f8a399c257`

class EditorSidebar extends Component {

  getTitle (product) {
    try {
      return (typeof product.title === 'object')
        ? limitCharacters(product.title.title, 12) // for Shopify products
        : limitCharacters(product.title, 12) // for regular products without integration
    } catch (e) {
      console.warn('FIXME: getting product title is incorrect')
      return 'unknown'
    }
  }

  onDeleteScenePressed = (e) => {
    e.stopPropagation()
    const { app, dispatch } = this.props
    const info = getTemplateInfo(app.templateId)
    console.log('onDeleteScenePressed', app.slidesCnt, info.minSlides)
    if (app.slidesCnt > info.minSlides) {
      dispatch(setNumberOfSlides(app, app.slidesCnt - 1))
    }
  }

  onAddScenePressed = () => {
    const { app, dispatch } = this.props
    const info = getTemplateInfo(app.templateId)
    console.log('onAddScenePressed', app.slidesCnt, info.maxSlides)
    if (app.slidesCnt < info.maxSlides) {
      dispatch(addSlide(app))
    }
  }

  renderImagePreview ({ index, imageUrl, isSmall, number, selected, products, onClick, canDelete }) {
    const { scenes, changeScene, selectedSceneNum, app } = this.props

    let selectedProductId
    if (products) { // Makes scene selected also if one of it's products scenes selected
      selected = products.reduce((previousValue, product) => {
        const productScene = scenes.findIndex(scene => (scene.id === product.id))
        if (productScene === selectedSceneNum) selectedProductId = product.id
        return previousValue || (productScene === selectedSceneNum)
      }, selected)
    }

    const info = getTemplateInfo(app.templateId)

    return (
      <Scene
        key={index}
        sceneUrl={imageUrl}
        isSmall={isSmall}
        number={number}
        selected={selected}
        onClick={onClick}
      >
        {products &&
          <ProductsContainer selected={selected}>
            {products.map(product => (
              <Button
                height='small'
                theme={(selectedProductId === product.id) ? 'fullblue' : 'whiteblueprimary'}
                onClick={e => {
                  changeScene(scenes.findIndex(scene => (scene.id === product.id)))
                  e.stopPropagation()
                }}
                key={product.id}
              >
                {this.getTitle(product)}
              </Button>
            ))}
          </ProductsContainer>
        }
        {canDelete && app.slidesCnt > info.minSlides &&
          <Tooltip
            content='Delete this scene'
            placement='right'
          >
            <DelSceneButtonContainer selected={selected}>
              <DelButton onClick={this.onDeleteScenePressed}>
                <ToggleOff size='8px' />
              </DelButton>
            </DelSceneButtonContainer>
          </Tooltip>
        }
      </Scene>
    )
  }

  renderTextPreview ({ index, text, selected, onClick }) {
    return (
      <TextContainer
        key={index}
        onClick={onClick}
      >
        <RadioGroup.Radio checked={selected} />
        <ProductLabel appearance='T1'>{text ? text.toUpperCase() : ''}</ProductLabel>
      </TextContainer>
    )
  }

  renderAddSceneButton (index, text) {
    const { app } = this.props
    const info = getTemplateInfo(app.templateId)
    if (app.slidesCnt < info.maxSlides) {
      return (
        <Tooltip
          content='Add a new scene'
          placement='right'
          key={index}
        >
          <Button
            height='small'
            prefixIcon={<Plus />}
            theme='emptyblue'
            onClick={this.onAddScenePressed}
          >
            {text}
          </Button>
        </Tooltip>
      )
    }
  }

  renderScenePreview () {
    let num = 0

    const { scenes, selectedSceneNum, changeScene, app, settings } = this.props
    // console.log('SideBar render scenes', scenes)
    return scenes.map((scene, index) => {

      const number = ('0' + (num + 1)).slice(-2) + '.'
      const selected = index === selectedSceneNum

      if (scene.siderbarPreview && scene.siderbarPreview.type) {
        switch (scene.siderbarPreview.type) {

          case sidebarSceneType.IMAGE:
            num++
            const imageUrl = _.get(app.customFields, scene.siderbarPreview.path, _.get(settings, scene.siderbarPreview.path))
            let products
            if (_.has(scene, ['siderbarPreview', 'products'])) {
              products = scene.siderbarPreview.products.map(product => ({
                title: _.get(app.customFields, product.path, _.get(settings, product.path)),
                id: product.id
              }))
            }
            const { isSmall } = scene.siderbarPreview
            const canDelete = scene.canDelete
            return this.renderImagePreview({ index, imageUrl, isSmall, number, selected, products, onClick: () => changeScene(index), canDelete })

          case sidebarSceneType.TEXT:
            const text = _.get(app.customFields, scene.siderbarPreview.path, _.get(settings, scene.siderbarPreview.path))
            return this.renderTextPreview({ index, text, selected, onClick: () => changeScene(index) })

          case sidebarSceneType.PRODUCT_NAME:
            if (api) {
              const product = _.get(app.customFields, scene.siderbarPreview.path, _.get(settings, scene.siderbarPreview.path))
              const pInfo = api.getProductInfo(product)
              return this.renderTextPreview({ index, text: pInfo.title, selected, onClick: () => changeScene(index) })
            }
            break

          case sidebarSceneType.ADD_SCENE_BUTTON:
            return this.renderAddSceneButton(index, scene.siderbarPreview.text)

          case sidebarSceneType.HIDDEN:
            return null

          default:
            break
        }
      }

      num++
      return this.renderImagePreview(index, defaultUrl, number, selected, () => changeScene(index))
    })
  }

  render () {
    return (
      <Container>
        <ScenesLabelContainer>
          <ScenesTitle>Jump to Scene</ScenesTitle>
          <Tooltip
            content='Quickly jump to any scene in your instant app by clicking a scene thumbnail below.'
            placement='right'
          >
            <IconCircle>?</IconCircle>
          </Tooltip>
        </ScenesLabelContainer>

        <div>{this.renderScenePreview()}</div>
      </Container>
    )
  }
}

EditorSidebar.propTypes = {
  scenes: PropTypes.array,
  selectedSceneNum: PropTypes.number.isRequired,
  changeScene: PropTypes.func,
  app: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
}

export default connect()(EditorSidebar)



// WEBPACK FOOTER //
// ./src/components/editor/sidebar/EditorSidebar.js